import {useCallback, useMemo, useRef, useState} from 'react';
import cx from 'classnames';
import HelpIcon from '@/assets/svg/flexBuilder/help.svg?tsx';
import CustomInitLogo from './custom-init-logo.svg?tsx';
import Logo from '../../icon/btn-flex-builder.svg?tsx';
import styles from './index.module.scss';
import type {IGetTemplateListResponse} from '../request.type';
import {utils as KuUtils} from '@/kuTools/sdk/utils';
import {LogIdMap, RuliuGroupId} from '../../constant';
import {openFlexBuilderInstructionDoc, openFlexBuilderRuLiuGroup} from '../../utils';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';

interface ITabConfig {
    key: string;
    text: string;
}

interface ICardConfig {
    key: string;
    imgSrc: string;
    text: string;
    templateId: string;
}

const keyOfAllCategory = 'all';
const {useIsInGray} = initGray(GRAY_RELEASE_KEYS.flexBuilder);

/**
 * 将服务端返回的数据格式化成前端需要的格式
 * @param templateListData 服务端返回的数据
 * @param activeTabKey 当前激活的 tab key
 */
function formatServerDataToLocal(
    templateListData: IGetTemplateListResponse,
    activeTabKey: string
): {
    tabConfig: ITabConfig[];
    cardConfig: ICardConfig[];
} {
    // 格式化标签数据
    const tabConfig: ITabConfig[] = templateListData.categories.map((category) => {
        return {
            key: category,
            text: category,
        };
    });
    // 在标签最前面加上【全部】标签
    tabConfig.unshift({
        key: keyOfAllCategory,
        text: '全部',
    });

    // 格式化卡片数据
    const cardConfig: ICardConfig[] = templateListData.templates.map(({
        templateId,
        cover,
        title,
        deleted,
        category,
    }) => {
        return {
            key: templateId,
            imgSrc: cover,
            text: title,
            deleted,
            category,
            templateId,
        };
    }).filter(({deleted, category}) => {
        // 过滤掉已删除的模板
        if (deleted === 1) {
            return false;
        }
        // 如果当前激活标签页为【全部】，则直接返回
        if (activeTabKey === keyOfAllCategory) {
            return true;
        }
        // 只返回当前激活标签页的模板
        return category === activeTabKey;
    });

    return {tabConfig, cardConfig};
}

interface IProps {
    /** 服务端返回的模板列表数据 */
    templateListData: IGetTemplateListResponse;
    builderId: string;
    /** 是否为随心搭卡片 */
    isCard?: boolean;
    /** 是否卡片视图 */
    isCardView?: boolean;
    /** 处理点击自定义生成 */
    handleCustom: () => void;
    /** 处理点击模板 */
    handleTemplate: (templateId: string) => void;
}

export const CustomInit = (props: IProps) => {
    const {templateListData, builderId, isCard, isCardView, handleCustom, handleTemplate} = props;
    const [activeTabKey, setActiveTabKey] = useState<string>(keyOfAllCategory);
    const descRef = useRef<HTMLSpanElement>(null);
    const [isShowDescTitle, setIsShowDescTitle] = useState(false);
    const isMatchFlexBuilder = useIsInGray();

    const handleTabClick = useCallback((key) => {
        return () => {
            setActiveTabKey(key);
        };
    }, []);

    // 格式化数据
    const {
        tabConfig,
        cardConfig,
    } = useMemo(() => {
        const data = formatServerDataToLocal(templateListData, activeTabKey);
        return data;
    }, [templateListData, activeTabKey]);

    const handleCustomCreate = () => {
        if (isMatchFlexBuilder) {
            KuUtils.kuLogger({
                repId: LogIdMap.SelectCustomFromCustomInit,
            });
            handleCustom?.();
            return;
        }

        KuUtils.kuLogger({
            repId: LogIdMap.ApplyCustomInit,
            params: {builderId},
        });
        openFlexBuilderInstructionDoc();
    };

    const handleTemplateCreate = (templateId: string, text: string) => {
        KuUtils.kuLogger({
            repId: LogIdMap.CreateComponentByTemplate,
            params: {
                from: 'custom',
                templateId,
                templateName: text,
            },
        });
        handleTemplate?.(templateId);
    };

    const handleOpenInstruction = () => {
        openFlexBuilderInstructionDoc();

        KuUtils.kuLogger({
            repId: LogIdMap.ClickGuidance,
            params: {
                pos: 'custom-init',
                type: 'guide',
            },
        });
    };

    const handleOpenGroup = () => {
        openFlexBuilderRuLiuGroup();

        KuUtils.kuLogger({
            repId: LogIdMap.ClickGuidance,
            params: {
                pos: 'custom-init',
                type: 'group',
            },
        });
    };

    const handleDescMouseEnter = () => {
        if (descRef.current) {
            setIsShowDescTitle(descRef.current.scrollWidth > descRef.current.clientWidth);
        }
    };

    return (
        <div
            className={cx(
                styles['custom-init-container'],
                isCard && isCardView && styles.isCard
            )}
        >
            {/* Logo */}
            <div className={styles['logo-container']}>
                <Logo className={styles['logo']} />
                <span className={styles['logo-text']}>随心搭 Beta</span>
                <div className={cx(styles.btn, styles.instruction)} onClick={handleOpenInstruction}>
                    <HelpIcon className={styles.icon} />
                    <span className={styles.btnText}>使用说明</span>
                </div>
                <div className={styles.btn} onClick={handleOpenGroup}>
                    <span className={styles.btnText}>群号{RuliuGroupId}</span>
                </div>
                <div className={styles.divide} />
                <span
                    ref={descRef}
                    className={styles.desc}
                    title={isShowDescTitle ? '点击“自定义”生成任意你需要的组件，也可直接复用现有模板' : ''}
                    onMouseEnter={handleDescMouseEnter}
                >
                    点击“自定义”生成任意你需要的组件，也可直接复用现有模板
                </span>
            </div>
            {/* 标签列表容器（TODO: MVP 版本暂时隐藏） */}
            {/* <div className={styles['tab-container']}>
                {
                    tabConfig.map(({key, text}) => {
                        return (
                            <div
                                key={key}
                                className={
                                    `${styles['tab-item']} ${activeTabKey === key ? styles['tab-item-active'] : ''}`
                                }
                                onClick={handleTabClick(key)}
                            >
                                <div className={styles['tab-item-text']}>
                                    <span>{text}</span>
                                </div>
                            </div>
                        );
                    })
                }
            </div> */}

            {/* 卡片列表容器 */}
            <div
                className={cx(
                    styles['card-scroll-container'],
                    isCard && isCardView && styles.isCard
                )}
            >
                <div className={styles['card-container']}>
                    {/* 自定义生成卡片 */}
                    <div className={styles['card-item']} onClick={handleCustomCreate}>
                        <div className={styles['card-item-img-container']}>
                            <div className={styles['card-item-custom-logo']}>
                                <CustomInitLogo />
                            </div>
                        </div>
                        <div className={styles['card-item-text']}>
                            {
                            isMatchFlexBuilder
                                ? <span>自定义</span>
                                : <span>申请自定义能力</span>
                        }
                        </div>
                    </div>
                    {/* 模板卡片列表 */}
                    {
                        cardConfig.map(({key, imgSrc, text, templateId}) => {
                            return (
                                <div
                                    key={key}
                                    className={`${styles['card-item']} ${styles['card-item-normal']}`}
                                    onClick={() => handleTemplateCreate(templateId, text)}
                                >
                                    <div className={styles['card-item-img-container']}>
                                        <img src={imgSrc} />
                                    </div>
                                    <div className={styles['card-item-text']}>
                                        <span>{text}</span>
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
            </div>
        </div>
    );
};
